import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { getFullName } from "@/lib/userUtils";
import { toastError } from "@/lib/toast";
import { createDmGroup, getOrCreateDm } from "@/lib/dmApi";
import { API_BASE_URL } from "@/lib/apiBase";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Unified new-chat picker: select people, then start.
 * 1 person → 1:1 DM; 2+ → group (title defaults to member names).
 */
export default function NewDmChatDialog({
  open,
  onOpenChange,
  orgId,
  accessToken,
  currentUserId,
  onCreated,
}) {
  const [memberQuery, setMemberQuery] = useState("");
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [starting, setStarting] = useState(false);

  const reset = () => {
    setMemberQuery("");
    setSelectedMemberIds([]);
  };

  useEffect(() => {
    if (!open || !accessToken || !orgId) return;
    let cancelled = false;
    (async () => {
      setMembersLoading(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/org/${orgId}/members`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!cancelled) setMembers(res.data || []);
      } catch (err) {
        console.error("Failed to load members for new chat:", err);
      } finally {
        if (!cancelled) setMembersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, accessToken, orgId]);

  const filteredMembers = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    return (members || [])
      .map((m) => {
        const u = m.user || m;
        if (!u?.user_id) return null;
        return {
          ...u,
          org_email: m.org_email ?? u.org_email ?? null,
          title: m.title ?? u.title ?? null,
        };
      })
      .filter(Boolean)
      .filter((u) => u.user_id !== currentUserId)
      .filter((u) => {
        if (!q) return true;
        const name = `${u.first_name || ""} ${u.last_name || ""}`
          .trim()
          .toLowerCase();
        return (
          name.includes(q) ||
          (u.org_email || "").toLowerCase().includes(q) ||
          (u.title || "").toLowerCase().includes(q)
        );
      });
  }, [members, memberQuery, currentUserId]);

  const selectedCount = selectedMemberIds.length;
  const isGroup = selectedCount >= 2;
  const canStart = selectedCount >= 1;

  const toggleMember = (userId) => {
    setSelectedMemberIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleOpenChange = (next) => {
    if (!next) reset();
    onOpenChange?.(next);
  };

  const handleStart = async () => {
    if (!accessToken || !orgId || !canStart || starting) return;
    setStarting(true);
    try {
      let conv;
      if (selectedCount === 1) {
        conv = await getOrCreateDm(
          accessToken,
          orgId,
          selectedMemberIds[0]
        );
      } else {
        // Title omitted — API defaults to member first names until renamed
        conv = await createDmGroup(accessToken, orgId, {
          memberUserIds: selectedMemberIds,
        });
      }
      handleOpenChange(false);
      await onCreated?.(conv);
    } catch (err) {
      console.error("Failed to start chat:", err);
      toastError(err, "Could not start conversation");
    } finally {
      setStarting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New chat</DialogTitle>
          <DialogDescription>
            Pick one person for a direct message, or several to start a group.
          </DialogDescription>
        </DialogHeader>
        <Input
          value={memberQuery}
          onChange={(e) => setMemberQuery(e.target.value)}
          placeholder="Search by name…"
          autoFocus
        />
        <div className="max-h-64 overflow-y-auto border rounded-md divide-y">
          {membersLoading ? (
            <p className="p-3 text-sm text-muted-foreground">Loading…</p>
          ) : filteredMembers.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">No members found</p>
          ) : (
            filteredMembers.map((u) => {
              const selected = selectedMemberIds.includes(u.user_id);
              return (
                <button
                  key={u.user_id}
                  type="button"
                  disabled={starting}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60 disabled:opacity-50 flex items-center justify-between gap-2"
                  onClick={() => toggleMember(u.user_id)}
                >
                  <span className="min-w-0 flex items-center gap-2 truncate">
                    <OrgMemberAvatar
                      user={u}
                      className="h-7 w-7"
                      fallbackClassName="text-[10px]"
                    />
                    <span className="font-medium truncate">{getFullName(u)}</span>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {selected ? "Selected" : "Add"}
                  </span>
                </button>
              );
            })
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {selectedCount === 0
              ? "Select people to message"
              : selectedCount === 1
                ? "1 person · direct message"
                : `${selectedCount} people · group`}
          </p>
          <Button
            type="button"
            disabled={starting || !canStart}
            onClick={handleStart}
          >
            {isGroup ? "Create group" : "Start chat"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
